import { ValidationError } from '~/errors';
import { uploadSessionRepository } from '~/repositories/uploadSessionRepository';
import { EntityType } from '~/swagger/portal/Api';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';
import type { UploadFileOptions } from '~/repositories/uploadSessionRepository';
import type {
  CreateCustomFieldDto,
  CustomFieldDto,
  CustomFieldPaginationDto,
  EntityCustomFieldsDto,
  SetCustomFieldValueDto,
  UpdateCustomFieldDto,
} from '~/swagger/portal/Api';
import type { ApiPaginationAndSearchFilter } from '~/types/apiFilters';

const CUSTOM_FIELDS_URL = '/api/custom-fields';

export function validateCustomField(customField: CreateCustomFieldDto | UpdateCustomFieldDto): void {
  if (!customField) throw new ValidationError('Custom field object must be provided');
}

export function validateCustomFieldId(customFieldId: string): void {
  if (!customFieldId) throw new ValidationError('Custom field ID must be provided');
}

export type CustomFieldsFilter = ApiPaginationAndSearchFilter<{
  entityType?: EntityType;
  public?: 'true' | 'false';
  filterable?: 'true' | 'false';
  active?: 'true' | 'false';
}>;

export class MagicTagsRepository extends BaseRestRepository {
  public async getCustomFields(filter?: CustomFieldsFilter): Promise<CustomFieldPaginationDto> {
    const url = urlWithQuery(CUSTOM_FIELDS_URL, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getCustomField(customFieldId: string): Promise<CustomFieldDto> {
    validateCustomFieldId(customFieldId);
    const url = `${CUSTOM_FIELDS_URL}/${customFieldId}`;
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async createCustomField(customField: CreateCustomFieldDto): Promise<CustomFieldDto> {
    validateCustomField(customField);
    const url = CUSTOM_FIELDS_URL;
    const response = await this.fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(customField),
    });
    return this.getJsonResponse(response);
  }

  public async updateCustomField(customFieldId: string, customField: UpdateCustomFieldDto): Promise<CustomFieldDto> {
    validateCustomFieldId(customFieldId);
    validateCustomField(customField);
    const url = `${CUSTOM_FIELDS_URL}/${customFieldId}`;
    const response = await this.fetchWithAuth(url, {
      method: 'PATCH',
      body: JSON.stringify(customField),
    });
    return this.getJsonResponse(response);
  }

  public async deleteCustomField(customFieldId: string): Promise<void> {
    validateCustomFieldId(customFieldId);
    const url = `${CUSTOM_FIELDS_URL}/${customFieldId}`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }

  static getEntityCustomFieldsUrlPrefix(entityType: EntityType, entityId: string): string {
    const entityTypeMap: Partial<Record<EntityType, string>> = {
      [EntityType.Property]: 'properties',
      [EntityType.Unit]: 'units',
      [EntityType.Lease]: 'leases',
      [EntityType.Vendor]: 'vendors',
      [EntityType.Owner]: 'owners',
      [EntityType.Tenant]: 'tenants',
      [EntityType.PropertyManager]: 'property-managers',
      [EntityType.UnitListing]: 'unit-listings',
      [EntityType.Portfolio]: 'portfolios',
      [EntityType.Company]: 'companies',
      [EntityType.MaintenanceRequest]: 'maintenance-requests',
      [EntityType.WorkOrder]: 'work-orders',
      [EntityType.Bill]: 'bills',
      [EntityType.Transaction]: 'transactions',
    };

    const _url = entityTypeMap[entityType];

    if (!_url) {
      throw new ValidationError(`Entity type ${entityType} is not supported`);
    }

    return `/api/${_url}/${entityId}/custom-fields`;
  }

  static getCustomFieldUploadSessionUrl(entityType: EntityType, entityId: string, customFieldId: string): string {
    return `${MagicTagsRepository.getEntityCustomFieldsUrlPrefix(entityType, entityId)}/${customFieldId}/upload-sessions`;
  }

  static buildCustomFieldUploadMethod(
    entityType: EntityType,
    entityId: string,
    customFieldId: string
  ): (file: File, options?: UploadFileOptions) => Promise<string> {
    const url = MagicTagsRepository.getCustomFieldUploadSessionUrl(entityType, entityId, customFieldId);
    return (file, options) => uploadSessionRepository.uploadFile(url, file, options);
  }

  // Entity Custom Field Values Operations
  public async getEntityCustomFieldValues(entityType: EntityType, entityId: string): Promise<EntityCustomFieldsDto> {
    if (!entityType) throw new ValidationError('Entity type must be provided');
    if (!entityId) throw new ValidationError('Entity ID must be provided');

    const url = MagicTagsRepository.getEntityCustomFieldsUrlPrefix(entityType, entityId);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async updateEntityCustomFieldValue(
    entityType: EntityType,
    entityId: string,
    customFieldId: string,
    value: SetCustomFieldValueDto
  ): Promise<void> {
    if (!entityType) throw new ValidationError('Entity type must be provided');
    if (!entityId) throw new ValidationError('Entity ID must be provided');
    validateCustomFieldId(customFieldId);
    if (!value) throw new ValidationError('Custom field value must be provided');

    const url = MagicTagsRepository.getEntityCustomFieldsUrlPrefix(entityType, entityId) + `/${customFieldId}`;
    await this.fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(value),
    });
  }

  public async removeEntityCustomFieldValue(entityType: EntityType, entityId: string, customFieldId: string): Promise<void> {
    if (!entityType) throw new ValidationError('Entity type must be provided');
    if (!entityId) throw new ValidationError('Entity ID must be provided');
    validateCustomFieldId(customFieldId);

    const url = MagicTagsRepository.getEntityCustomFieldsUrlPrefix(entityType, entityId) + `/${customFieldId}`;
    await this.fetchWithAuth(url, { method: 'DELETE' });
  }
}

export const magicTagsRepository = new MagicTagsRepository();
